// ── Editorial Core Components ─────────────────────────────────────────
// Pixelgenau aus design_handoff_red_webapp/source/red-ui.jsx + red-components.jsx.
// Nutzbar auf Webapp UND Mobile — Sharp 3px, kein farbiger Glow, keine Verläufe.
// Strings sind neutralisiert (kein "§ 23", kein "steuerpflichtig") — Memory-konform.

import React, { useState, useEffect, useRef, useId } from 'react';
import { useTheme, useUI } from '../../context/ThemeContext';
import { F, MONO_FEAT, R, withAlpha, makeAccent } from '../../styles/theme';
import { Icon, Icons } from './Icons';

// ── Caps — Eyebrow-Labels (Jakarta, 700, weite Letter-Spacing, uppercase) ──
export function Caps({ children, color, size = 10.5, ls = 0.14, style }) {
  return (
    <span style={{
      fontFamily: F.family,
      fontSize: size,
      fontWeight: 700,
      letterSpacing: `${ls}em`,
      textTransform: 'uppercase',
      color: color || '#9090A0',
      ...style,
    }}>{children}</span>
  );
}

// ── CropMark — rote Eck-Klammer für "amtliche Dokumente" ──
export function CropMark({ corner, size = 16, thickness = 1.5, color, offset = 0 }) {
  const s = { position: 'absolute', width: size, height: size, pointerEvents: 'none' };
  const c = `${thickness}px solid ${color || '#E4544A'}`;
  if (corner === 'tl') Object.assign(s, { top: offset, left: offset, borderTop: c, borderLeft: c });
  if (corner === 'tr') Object.assign(s, { top: offset, right: offset, borderTop: c, borderRight: c });
  if (corner === 'bl') Object.assign(s, { bottom: offset, left: offset, borderBottom: c, borderLeft: c });
  if (corner === 'br') Object.assign(s, { bottom: offset, right: offset, borderBottom: c, borderRight: c });
  return <div style={s} />;
}

// ── Card — scharf 3px, T.line border, T.surface, KEIN Schatten ──
export function Card({ children, pad = 18, style, ...rest }) {
  const { T } = useTheme();
  const ui = useUI();
  return (
    <div
      {...rest}
      style={{
        background: T.surface,
        border: `1px solid ${T.line}`,
        borderRadius: ui.isMobile ? ui.radius.card : R.sharp,
        padding: pad,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ── SectionHead — Eyebrow + Haarlinie + Display-Titel mit 2px-Unterkante ──
// (Kapitel-Nummern bewusst entfernt — Handoff-Spec: "keine Kapitel-Nummer mehr")
export function SectionHead({ eyebrow, title, sub, right, ed }) {
  const { T } = useTheme();
  const accent = makeAccent(T.signal || T.red);
  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
        {eyebrow && (
          <Caps color={accent.signal} size={10} ls={0.16}>{eyebrow}</Caps>
        )}
        <div style={{ flex: 1, height: 1, background: T.line }} />
        {right}
      </div>
      {title && (
        <h1 style={{
          fontFamily: F.display,
          fontSize: ed && ed.pageTitle ? ed.pageTitle : 'clamp(28px, 3.4vw, 44px)',
          fontWeight: 800,
          letterSpacing: '-0.04em',
          lineHeight: 1.05,
          margin: 0,
          color: T.text,
          paddingBottom: 16,
          borderBottom: `2px solid ${T.text}`,
          display: 'inline-block',
        }}>{title}</h1>
      )}
      {sub && (
        <div style={{
          marginTop: 14,
          fontSize: 14,
          color: T.sub,
          maxWidth: 720,
          lineHeight: 1.55,
        }}>{sub}</div>
      )}
    </div>
  );
}

// ── Btn — primary (solid signal), ghost (1.5px border), soft (tint), light ──
// Pfeil-Glyph IMMER "›" (kein "→"), gleitet 2px nach rechts bei Hover.
export function Btn({
  children, onClick, variant = 'primary', size = 'md',
  icon, iconRight, full = false, disabled = false, style, ...rest
}) {
  const { T } = useTheme();
  const ui = useUI();
  const accent = makeAccent(T.signal || T.red);
  const [hover, setHover] = useState(false);

  const sizes = {
    sm: { padY: 8, padX: 12, fs: 12.5 },
    md: { padY: 11, padX: 16, fs: 13.5 },
    lg: { padY: 14, padX: 20, fs: 14.5 },
  };
  const sz = sizes[size] || sizes.md;

  let bg, color, border;
  if (variant === 'primary') {
    bg = hover ? accent.dark : accent.signal;
    color = '#fff';
    border = `1px solid ${hover ? accent.dark : accent.signal}`;
  } else if (variant === 'ghost') {
    bg = hover ? T.surfaceHi : 'transparent';
    color = T.text;
    border = `1.5px solid ${T.line}`;
  } else if (variant === 'soft') {
    bg = hover ? accent.a(0.18) : accent.a(0.12);
    color = accent.signal;
    border = `1px solid transparent`;
  } else if (variant === 'light') {
    bg = hover ? '#F4F4F8' : '#fff';
    color = accent.signal;
    border = `1px solid transparent`;
  } else {
    bg = 'transparent'; color = T.text; border = 'none';
  }

  return (
    <button
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        width: full ? '100%' : 'auto',
        padding: `${sz.padY}px ${sz.padX}px`,
        background: bg,
        color,
        border,
        borderRadius: ui.isMobile ? ui.radius.control : R.sharp,
        fontFamily: F.family,
        fontSize: sz.fs,
        fontWeight: 700,
        letterSpacing: '-0.01em',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        WebkitTapHighlightColor: 'transparent',
        transition: 'background 0.16s ease, border-color 0.16s ease',
        ...style,
      }}
      {...rest}
    >
      {icon && <Icon d={Icons[icon] || icon} size={15} stroke={color} sw={2.4} />}
      {children}
      {iconRight && (
        <span style={{
          display: 'inline-block',
          transform: hover ? 'translateX(2px)' : 'translateX(0)',
          transition: 'transform 0.16s ease',
          fontWeight: 700,
        }}>
          {iconRight === 'arrow' ? '›' : <Icon d={Icons[iconRight] || iconRight} size={15} stroke={color} sw={2.4} />}
        </span>
      )}
    </button>
  );
}

// ── Pill — scharf 3px, aktiv solid signal ──
export function Pill({ children, active = false, onClick, icon, style }) {
  const { T } = useTheme();
  const ui = useUI();
  const accent = makeAccent(T.signal || T.red);
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        padding: '7px 13px',
        background: active ? accent.signal : (hover ? T.surfaceHi : T.surface),
        color: active ? '#fff' : T.sub,
        border: `1px solid ${active ? accent.signal : T.line}`,
        borderRadius: ui.isMobile ? ui.radius.control : R.sharp,
        fontFamily: F.family,
        fontSize: 12.5,
        fontWeight: 700,
        letterSpacing: '-0.01em',
        cursor: 'pointer',
        WebkitTapHighlightColor: 'transparent',
        transition: 'background 0.16s ease, color 0.16s ease',
        ...style,
      }}
    >
      {icon && <Icon d={Icons[icon] || icon} size={13} stroke={active ? '#fff' : T.sub} sw={2.4} />}
      {children}
    </button>
  );
}

// ── Segmented — iOS-Style Control mit gleitendem Thumb ──
// Vertiefter, neutraler Track; ein angehobener Thumb (weiß im Light-, lifted
// grau im Dark-Mode) gleitet via framer-motion-`layoutId` zwischen den
// Segmenten (iOS-Feder). Aktiv-Zustand kommt aus Thumb-Elevation + Schrift-
// farbe — KEIN roter Container (siehe feedback_no_pills_borders_glow).
// Bei vielen Tabs (z.B. WalletScreen) scrollt das aktive Segment in Sicht.
export function Segmented({ value, onChange, options, style, fluid = false, bare = false }) {
  const { T } = useTheme();
  const ui = useUI();
  // Eindeutige layoutId pro Instanz, sonst „springt" der Thumb zwischen
  // mehreren Segmented-Controls auf derselben Seite.
  const thumbId = useId();
  // Rundung wie die Navbar-Pille (gleitender Aktiv-Thumb): control-Radius auf
  // BEIDEN Surfaces (Desktop nicht mehr R.sharp — User-Wunsch „runderer Motion-
  // Container von der Navbar"). thumbRadius leitet sich daraus ab (−3).
  const trackRadius = ui.radius.control;
  // bare = ohne Track-Box (transparent, kein Padding) → leichter/cleaner für
  // kleine Toggles (Chart-Range, Berichte-Jahre). Roter Aktiv-Pill bleibt.
  return (
    <div style={{
      display: fluid ? 'flex' : 'inline-flex',
      width: fluid ? '100%' : undefined,
      gap: bare ? 20 : 16,
      padding: 0,
      background: 'transparent',
      ...style,
    }}>
      {options.map(opt => {
        const val = typeof opt === 'string' ? opt : opt.value;
        const label = typeof opt === 'string' ? opt : opt.label;
        const active = value === val;
        return (
          <SegBtn
            key={val}
            active={active}
            onClick={() => onChange(val)}
            label={label}
            T={T}
            fluid={fluid}
            thumbId={thumbId}
            trackRadius={trackRadius}
          />
        );
      })}
    </div>
  );
}

// Einzelnes Segment. Der aktive Button trägt den gemeinsamen Thumb
// (layoutId) — beim Wechsel animiert framer-motion ihn von alt nach neu.
function SegBtn({ active, onClick, label, T, fluid, thumbId, trackRadius }) {
  const [hover, setHover] = useState(false);
  const btnRef = useRef(null);

  // Aktives Segment in den sichtbaren Bereich scrollen (horizontaler
  // Overflow bei vielen Tabs). block:'nearest' vermeidet vertikalen Sprung.
  useEffect(() => {
    if (active && btnRef.current) {
      btnRef.current.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }
  }, [active]);

  return (
    <button
      ref={btnRef}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative',
        // Stacking-Context pro Button: der AKTIVE (mit gleitendem Thumb) liegt
        // BEWUSST unter den inaktiven (z:0 < z:1). So bleibt framers temporäre
        // z-index-Anhebung des layoutId-Thumbs INNERHALB des aktiven Buttons
        // gefangen → der rote Thumb kann beim Gleiten NIE die Labels der Buttons
        // überdecken, über die er hinweggleitet (Text verschwand sonst kurz).
        zIndex: active ? 0 : 1,
        flex: fluid ? 1 : undefined,
        flexShrink: 0,
        padding: '4px 0',
        background: 'transparent',
        // Calm-Mock: reiner Text — aktiv ink/fett, inaktiv blass. Kein Pill, kein Track.
        color: active ? T.text : (hover ? T.text : T.mute),
        border: 'none',
        fontFamily: F.family,
        fontSize: 13,
        fontWeight: active ? 700 : 600,
        letterSpacing: '-0.01em',
        textAlign: 'center',
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        WebkitTapHighlightColor: 'transparent',
        transition: 'color 0.2s ease',
      }}
    >
      <span style={{ position: 'relative', zIndex: 1 }}>{label}</span>
    </button>
  );
}

// ── usePillMotion — Hover/Press-State + Transform/Transition für Pill-Buttons.
// EINE Button-Motion app-weit = wie die Navbar (usePress): nur Press-Squish
// (scale 0.96), KEIN Hover-Lift/-Scale (das „springende" Mouseover ist raus).
// Hover wechselt nur Fläche/Farbe (via hover-State im Button-Style).
export function usePillMotion() {
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);
  return {
    hover,
    pressed,
    handlers: {
      onMouseEnter: () => setHover(true),
      onMouseLeave: () => { setHover(false); setPressed(false); },
      onMouseDown: () => setPressed(true),
      onMouseUp: () => setPressed(false),
    },
    motion: {
      transform: pressed ? 'scale(0.96)' : 'scale(1)',
      transition: 'background 0.18s ease, color 0.18s ease, border-color 0.18s ease, transform 0.14s cubic-bezier(0.22, 1, 0.36, 1)',
    },
  };
}

// ── Delta — ▲/▼ + Wert, gain pastel green / loss pastel red ──
export function Delta({ value, size = 13, showArrow = true, dec = 2 }) {
  const { T } = useTheme();
  if (value == null || !isFinite(value)) {
    return <span style={{ fontFamily: F.num, fontSize: size, color: T.mute }}>—</span>;
  }
  const pos = value >= 0;
  const color = pos ? (T.gain || T.green) : (T.loss || T.red);
  return (
    <span style={{
      ...MONO_FEAT,
      fontSize: size,
      fontWeight: 700,
      color,
      letterSpacing: '-0.01em',
      display: 'inline-flex',
      alignItems: 'baseline',
      gap: 4,
    }}>
      {showArrow && <span style={{ fontSize: size * 0.7 }}>{pos ? '▲' : '▼'}</span>}
      {pos ? '+' : '−'}{Math.abs(value).toFixed(dec)}%
    </span>
  );
}

// ── EmptyState — zentriert, Icon-Box + Titel + Sub + Action ──
export function EmptyState({ icon = 'search', title, sub, action }) {
  const { T } = useTheme();
  const ui = useUI();
  return (
    <div style={{
      padding: '48px 24px',
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 6,
    }}>
      <span style={{
        width: 52, height: 52,
        border: `1px solid ${T.line}`,
        borderRadius: ui.isMobile ? ui.radius.tile : R.sharp,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 8,
      }}>
        <Icon d={Icons[icon] || icon} size={22} stroke={T.mute} sw={2.4} />
      </span>
      <div style={{ fontFamily: F.family, fontSize: 15, fontWeight: 700, color: T.text }}>{title}</div>
      {sub && <div style={{ fontSize: 13, color: T.sub, maxWidth: 320, lineHeight: 1.5 }}>{sub}</div>}
      {action && <div style={{ marginTop: 10 }}>{action}</div>}
    </div>
  );
}

// ── Badge — kompaktes Caps-Label mit dezenter Tönung ──
export function Badge({ children, tone = 'default' }) {
  const { T } = useTheme();
  const accent = makeAccent(T.signal || T.red);
  let c = T.sub, bd = T.line, bg = 'transparent';
  if (tone === 'green') { c = T.gain || T.green; bd = withAlpha(c, 0.4); bg = withAlpha(c, 0.10); }
  else if (tone === 'accent') { c = accent.signal; bd = accent.a(0.4); bg = accent.a(0.10); }
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '2px 7px', borderRadius: 2,
      border: `1px solid ${bd}`, background: bg, color: c,
      fontFamily: F.family, fontSize: 10, fontWeight: 700,
      letterSpacing: '0.06em', textTransform: 'uppercase',
    }}>
      {children}
    </span>
  );
}

// ── Dropdown — Click-Trigger + Menü ──
export function Dropdown({ trigger, items, align = 'right' }) {
  const { T } = useTheme();
  const accent = makeAccent(T.signal || T.red);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div onClick={() => setOpen(o => !o)}>{trigger}</div>
      {open && (
        <div role="menu" style={{
          position: 'absolute',
          top: 'calc(100% + 6px)',
          [align]: 0,
          zIndex: 60, minWidth: 180,
          background: T.surface,
          border: `1px solid ${T.line}`,
          borderRadius: R.sharp,
          boxShadow: T.cardShadow,
          overflow: 'hidden',
        }}>
          {items.map((it, i) => (
            <button key={i} role="menuitem"
              onClick={() => { setOpen(false); it.onClick && it.onClick(); }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 13px',
                background: 'transparent', border: 'none',
                borderTop: i ? `1px solid ${T.lineSoft || T.line}` : 'none',
                color: it.danger ? accent.signal : T.text,
                fontFamily: F.family, fontSize: 13, fontWeight: 600,
                textAlign: 'left', cursor: 'pointer',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = T.surfaceHi}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              {it.icon && <Icon d={Icons[it.icon] || it.icon} size={15} stroke={it.danger ? accent.signal : T.sub} sw={2.4} />}
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── MetaStrip — kleine Daten-Reihe mit vertikalen Haarlinien zwischen Items ──
export function MetaStrip({ items }) {
  const { T } = useTheme();
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 0,
      flexWrap: 'wrap',
    }}>
      {items.map((it, i) => (
        <React.Fragment key={i}>
          {i > 0 && <div style={{ width: 1, height: 22, background: T.line, margin: '0 16px' }} />}
          <div>
            <Caps color={T.mute} size={9.5} ls={0.14}>{it.label}</Caps>
            <div style={{
              ...MONO_FEAT,
              fontSize: 14, fontWeight: 700,
              color: it.color || T.text,
              letterSpacing: '-0.02em',
              marginTop: 4,
            }}>{it.value}</div>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}
